import api from './api';

export const callsService = {
  getAllCalls: async (params = {}) => {
    try {
      const response = await api.get('/calls', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting call records:', error);
      throw error;
    }
  },

  getCallById: async (id) => {
    try {
      const response = await api.get(`/calls/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error getting call record:', error);
      throw error;
    }
  },

  createCall: async (callData) => {
    try {
      const response = await api.post('/calls', callData);
      return response.data;
    } catch (error) {
      console.error('Error creating call record:', error);
      throw error;
    }
  },

  updateCall: async (id, callData) => {
    try {
      const response = await api.put(`/calls/${id}`, callData);
      return response.data;
    } catch (error) {
      console.error('Error updating call record:', error);
      throw error;
    }
  },

  deleteCall: async (id) => {
    try {
      const response = await api.delete(`/calls/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting call record:', error);
      throw error;
    }
  },

  bulkDeleteCalls: async (callIds) => {
    try {
      const response = await api.delete('/calls/bulk', {
        data: { callIds }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting call records:', error);
      throw error;
    }
  },

  getCallStats: async (startDate, endDate) => {
    try {
      const response = await api.get('/calls/stats', { 
        params: { startDate, endDate } 
      });
      return response.data;
    } catch (error) {
      console.error('Error getting call stats:', error);
      throw error;
    }
  },

  getCallsByStatus: async (status) => {
    try {
      const response = await api.get(`/calls/status/${status}`);
      return response.data;
    } catch (error) {
      console.error('Error getting calls by status:', error);
      throw error;
    }
  },

  getCallsByType: async (type) => {
    try {
      const response = await api.get(`/calls/type/${type}`);
      return response.data;
    } catch (error) {
      console.error('Error getting calls by type:', error);
      throw error;
    }
  },

  getCallsByOutcome: async (outcome) => {
    try {
      const response = await api.get(`/calls/outcome/${outcome}`);
      return response.data;
    } catch (error) {
      console.error('Error getting calls by outcome:', error);
      throw error;
    }
  },

  getCallsByAssignee: async (assigneeId) => {
    try {
      const response = await api.get(`/calls/assignee/${assigneeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting calls by assignee:', error);
      throw error;
    }
  },

  getCallsByDuration: async (minDuration, maxDuration) => {
    try {
      const response = await api.get('/calls/duration', {
        params: { minDuration, maxDuration }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting calls by duration:', error);
      throw error;
    }
  },

  getCallCode: async () => {
    try {
      const response = await api.get('/auto-increment/curr', {
        params: { sequenceName: 'CALL' }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting call code:', error);
      throw error;
    }
  },

  startCall: async (id) => {
    try {
      const response = await api.post(`/calls/${id}/start`);
      return response.data;
    } catch (error) {
      console.error('Error starting call:', error);
      throw error;
    }
  },

  endCall: async (id, callData) => {
    try {
      const response = await api.post(`/calls/${id}/end`, callData);
      return response.data;
    } catch (error) {
      console.error('Error ending call:', error);
      throw error;
    }
  },

  recordCall: async (id, recordingData) => {
    try {
      const response = await api.post(`/calls/${id}/record`, recordingData);
      return response.data;
    } catch (error) {
      console.error('Error recording call:', error);
      throw error;
    }
  },

  transcribeCall: async (id) => {
    try {
      const response = await api.post(`/calls/${id}/transcribe`);
      return response.data;
    } catch (error) {
      console.error('Error transcribing call:', error);
      throw error;
    }
  },

  // Call validation helpers
  validateCallData: (callData) => {
    const errors = [];
    
    if (!callData.subject || callData.subject.trim() === '') {
      errors.push('Subject is required');
    }
    
    if (!callData.callerName || callData.callerName.trim() === '') {
      errors.push('Caller name is required');
    }
    
    if (!callData.callerPhone || callData.callerPhone.trim() === '') {
      errors.push('Caller phone is required');
    }
    
    if (!callData.callPurpose || callData.callPurpose.trim() === '') {
      errors.push('Call purpose is required');
    }
    
    // Validate phone number format
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (callData.callerPhone && !phoneRegex.test(callData.callerPhone.replace(/[\s\-\(\)]/g, ''))) {
      errors.push('Invalid phone number format');
    }
    
    // Validate email format if provided
    if (callData.callerEmail && callData.callerEmail.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(callData.callerEmail)) {
        errors.push('Invalid email format');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Call formatting helpers
  formatCallForDisplay: (call) => {
    return {
      ...call,
      formattedDate: new Date(call.callDate).toLocaleDateString(),
      formattedTime: new Date(call.callTime).toLocaleTimeString(),
      formattedDuration: call.callDuration ? 
        `${Math.floor(call.callDuration / 60)}:${(call.callDuration % 60).toString().padStart(2, '0')}` : 
        'N/A',
      statusColor: {
        'Scheduled': 'text-blue-600',
        'In Progress': 'text-yellow-600',
        'Completed': 'text-green-600',
        'Missed': 'text-red-600',
        'Cancelled': 'text-gray-600'
      }[call.callStatus] || 'text-gray-600',
      outcomeColor: {
        'Successful': 'text-green-600',
        'Follow-up Required': 'text-blue-600',
        'No Answer': 'text-yellow-600',
        'Busy': 'text-orange-600',
        'Not Interested': 'text-red-600'
      }[call.callOutcome] || 'text-gray-600',
      priorityColor: {
        'High': 'text-red-600',
        'Medium': 'text-yellow-600',
        'Low': 'text-green-600'
      }[call.priority] || 'text-gray-600'
    };
  },

  // Call search helpers
  searchCalls: async (searchTerm, filters = {}) => {
    try {
      const params = {
        search: searchTerm,
        ...filters
      };
      const response = await api.get('/calls', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching calls:', error);
      throw error;
    }
  },

  // Call filtering helpers
  getCallFilters: () => {
    return {
      status: ['Scheduled', 'In Progress', 'Completed', 'Missed', 'Cancelled'],
      type: ['Outbound', 'Inbound', 'Follow-up', 'Support', 'Sales', 'Marketing'],
      outcome: ['Successful', 'Follow-up Required', 'No Answer', 'Busy', 'Not Interested'],
      priority: ['High', 'Medium', 'Low'],
      method: ['Phone Call', 'Video Call', 'Conference Call', 'VoIP', 'Mobile']
    };
  },

  // Call analytics helpers
  calculateCallMetrics: (calls) => {
    const totalCalls = calls.length;
    const completedCalls = calls.filter(call => call.callStatus === 'Completed').length;
    const missedCalls = calls.filter(call => call.callStatus === 'Missed').length;
    const successfulCalls = calls.filter(call => call.callOutcome === 'Successful').length;
    const followUpRequired = calls.filter(call => call.callOutcome === 'Follow-up Required').length;
    const totalDuration = calls.reduce((sum, call) => sum + (call.callDuration || 0), 0);
    const averageDuration = totalCalls > 0 ? totalDuration / totalCalls : 0;

    return {
      totalCalls,
      completedCalls,
      missedCalls,
      successfulCalls,
      followUpRequired,
      totalDuration,
      averageDuration: Math.round(averageDuration),
      completionRate: totalCalls > 0 ? (completedCalls / totalCalls * 100).toFixed(2) : 0,
      successRate: completedCalls > 0 ? (successfulCalls / completedCalls * 100).toFixed(2) : 0,
      followUpRate: completedCalls > 0 ? (followUpRequired / completedCalls * 100).toFixed(2) : 0
    };
  }
};
